import { useState, useEffect, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiClient } from "@/lib/api/client";
import { IndicatorDto, CreateIndicatorDto, UpdateIndicatorDto, DepartmentDto, IndicatorFrequencyDto, IndicatorFilterParams } from "@/lib/types/api";
import { handleApiError } from "@/lib/utils/errors";
import { SEARCH_DEBOUNCE_MS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, Column } from "@/components/datatable/DataTable";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, BarChart3, Search, Eye } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { StatusBadge } from "@/components/ui/status-badge";

const indicatorSchema = z.object({
  indicatorName: z.string().min(1, "Indicator name is required"),
  departmentId: z.string().min(1, "Department is required"),
  frequencyId: z.string().min(1, "Frequency is required"),
  unit: z.string().min(1, "Unit is required"),
  numeratorLabel: z.string().optional(),
  denominatorLabel: z.string().optional(),
  description: z.string().optional(),
  isBlockLevel: z.boolean().optional(),
  programType: z.string().min(1, "Program is required"),
});

type IndicatorFormData = z.infer<typeof indicatorSchema>;

export default function IndicatorsPage() {
  const [indicators, setIndicators] = useState<IndicatorDto[]>([]);
  const [departments, setDepartments] = useState<DepartmentDto[]>([]);
  const [frequencies, setFrequencies] = useState<IndicatorFrequencyDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState<string>("");
  const [selectedFrequency, setSelectedFrequency] = useState<string>("");
  const [selectedProgram, setSelectedProgram] = useState<string>("");
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedIndicator, setSelectedIndicator] = useState<IndicatorDto | null>(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState<string>("all");
  const [frequencyFilter, setFrequencyFilter] = useState<string>("all");
  const [blockLevelFilter, setBlockLevelFilter] = useState<string>("all");
  const [programTypeFilter, setProgramTypeFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("indicatorName");
  const [sortDescending, setSortDescending] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm<IndicatorFormData>({
    resolver: zodResolver(indicatorSchema),
  });

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const filterParams: IndicatorFilterParams = {
        pageNumber: currentPage,
        pageSize: pageSize,
        search: searchQuery || undefined,
        departmentId: departmentFilter !== "all" ? departmentFilter : undefined,
        frequencyId: frequencyFilter !== "all" ? frequencyFilter : undefined,
        isBlockLevel: blockLevelFilter === "all" ? undefined : blockLevelFilter === "true",
        programType: programTypeFilter !== "all" ? programTypeFilter : undefined,
        sortBy: sortBy,
        sortDescending: sortDescending,
      };

      const [indicatorsRes, departmentsRes, frequenciesRes] = await Promise.all([
        apiClient.getIndicatorsPagedWithFilters(filterParams),
        apiClient.getDepartments(),
        apiClient.getIndicatorFrequencies(),
      ]);
      
      if (indicatorsRes.success && indicatorsRes.data) {
        setIndicators(indicatorsRes.data.items || []);
        setTotalCount(indicatorsRes.data.totalCount || 0);
        setTotalPages(Math.ceil((indicatorsRes.data.totalCount || 0) / pageSize));
      }
      if (departmentsRes.success && departmentsRes.data) {
        setDepartments(departmentsRes.data);
      }
      if (frequenciesRes.success && frequenciesRes.data) {
        setFrequencies(frequenciesRes.data);
      }
    } catch (err: unknown) {
      setError(handleApiError(err, "Failed to load data"));
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, departmentFilter, frequencyFilter, blockLevelFilter, programTypeFilter, sortBy, sortDescending]);

  // Single effect to handle all data loading with proper debouncing
  useEffect(() => {
    // For page/pageSize changes while filters are active, load immediately
    const hasActiveFilters = searchQuery !== "" || departmentFilter !== "all" || frequencyFilter !== "all" || 
                              blockLevelFilter !== "all" || programTypeFilter !== "all" || 
                              sortBy !== "indicatorName" || sortDescending;
    
    if (!hasActiveFilters) {
      // No filters active - load immediately
      loadData();
      return;
    }

    // For search/filter/sort changes, debounce and reset to page 1
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        loadData();
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchQuery, departmentFilter, frequencyFilter, blockLevelFilter, programTypeFilter, sortBy, sortDescending]);

  // Separate effect for page changes - always load immediately without debounce
  useEffect(() => {
    const hasActiveFilters = searchQuery !== "" || departmentFilter !== "all" || frequencyFilter !== "all" || 
                              blockLevelFilter !== "all" || programTypeFilter !== "all" || 
                              sortBy !== "indicatorName" || sortDescending;
    
    if (hasActiveFilters) {
      loadData();
    }
  }, [currentPage, pageSize]);

  const onSubmit = async (data: IndicatorFormData) => {
    try {
      setError("");
      if (editingId) {
        const updateData: UpdateIndicatorDto = {
          indicatorName: data.indicatorName,
          departmentId: data.departmentId,
          description: data.description,
          frequencyId: data.frequencyId,
          unit: data.unit,
          numeratorLabel: data.numeratorLabel,
          denominatorLabel: data.denominatorLabel,
          isBlockLevel: data.isBlockLevel || false,
          programType: data.programType,
          isActive: true,
        };
        await apiClient.updateIndicator(editingId, updateData);
      } else {
        const createData: CreateIndicatorDto = {
          indicatorName: data.indicatorName,
          departmentId: data.departmentId,
          frequencyId: data.frequencyId,
          unit: data.unit,
          numeratorLabel: data.numeratorLabel,
          denominatorLabel: data.denominatorLabel,
          description: data.description,
          isBlockLevel: data.isBlockLevel || false,
          programType: data.programType,
        };
        await apiClient.createIndicator(createData);
      }
      setShowForm(false);
      setEditingId(null);
      reset();
      loadData();
    } catch (err: unknown) {
      setError(handleApiError(err, "Operation failed"));
    }
  };

  const handleEdit = (indicator: IndicatorDto) => {
    setEditingId(indicator.id);
    setSelectedDepartment(indicator.departmentId);
    setSelectedFrequency(indicator.frequencyId);
    setSelectedProgram(indicator.programType || "");
    reset({
      indicatorName: indicator.indicatorName,
      departmentId: indicator.departmentId,
      frequencyId: indicator.frequencyId,
      unit: indicator.unit,
      numeratorLabel: indicator.numeratorLabel || "",
      denominatorLabel: indicator.denominatorLabel || "",
      description: indicator.description || "",
      isBlockLevel: indicator.isBlockLevel,
      programType: indicator.programType || "",
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this indicator?")) return;

    try {
      await apiClient.deleteIndicator(id);
      if (indicators.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        loadData();
      }
    } catch (err: unknown) {
      setError(handleApiError(err, "Delete failed"));
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setSelectedDepartment("");
    setSelectedFrequency("");
    setSelectedProgram("");
    reset({
      indicatorName: "",
      departmentId: "",
      frequencyId: "",
      unit: "",
      numeratorLabel: "",
      denominatorLabel: "",
      description: "",
      isBlockLevel: false,
      programType: "",
    });
    setError("");
  };

  const handleViewDetails = (indicator: IndicatorDto) => {
    setSelectedIndicator(indicator);
    setShowDetailsModal(true);
  };

  const getDepartmentName = (departmentId: string) => {
    return departments.find(d => d.id === departmentId)?.departmentName || "Unknown";
  };

  const getFrequencyName = (frequencyId: string) => {
    return frequencies.find(f => f.id === frequencyId)?.frequencyName || "Unknown";
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDescending(!sortDescending);
    } else {
      setSortBy(column);
      setSortDescending(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-100 border border-red-200 dark:border-red-300 rounded-md">
          {error}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={(open) => !open && handleCancel()}>
        <DialogContent onClose={handleCancel}>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Indicator" : "New Indicator"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label="Indicator Name"
              placeholder="e.g., Student Enrollment Rate"
              error={errors.indicatorName?.message}
              {...register("indicatorName")}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Department
              </label>
              <Select
                value={selectedDepartment}
                onValueChange={(value) => {
                  setSelectedDepartment(value);
                  setValue("departmentId", value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select department..." />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.departmentName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.departmentId && (
                <p className="mt-1 text-sm text-red-600">{errors.departmentId.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Program
              </label>
              <Select
                value={selectedProgram}
                onValueChange={(value) => {
                  setSelectedProgram(value);
                  setValue("programType", value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select program..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Aspirational District Program">Aspirational District Program</SelectItem>
                  <SelectItem value="Aspirational Block Program">Aspirational Block Program</SelectItem>
                </SelectContent>
              </Select>
              {errors.programType && (
                <p className="mt-1 text-sm text-red-600">{errors.programType.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Unit"
                placeholder="e.g., Percentage, Count"
                error={errors.unit?.message}
                {...register("unit")}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Frequency
                </label>
                <Select
                  value={selectedFrequency}
                  onValueChange={(value) => {
                    setSelectedFrequency(value);
                    setValue("frequencyId", value);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select frequency..." />
                  </SelectTrigger>
                  <SelectContent>
                    {frequencies.map((freq) => (
                      <SelectItem key={freq.id} value={freq.id}>
                        {freq.frequencyName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.frequencyId && (
                  <p className="mt-1 text-sm text-red-600">{errors.frequencyId.message}</p>
                )}
              </div>
            </div>

            <Input
                label="Numerator Label"
                placeholder="e.g., Number of students"
                error={errors.numeratorLabel?.message}
                {...register("numeratorLabel")}
              />

            <Input
              label="Denominator Label"
              placeholder="e.g., Total population"
              error={errors.denominatorLabel?.message}
              {...register("denominatorLabel")}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
                placeholder="Indicator description..."
                {...register("description")}
              />
            </div>

            <Controller
              name="isBlockLevel"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <Switch
                  label="Block Level Indicator"
                  id="isBlockLevel"
                  yesLabel="Yes"
                  noLabel="No"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit">{editingId ? "Update" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Indicator Details</DialogTitle>
          </DialogHeader>
          {selectedIndicator && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Indicator Name</label>
                  <p className="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">{selectedIndicator.indicatorName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Department</label>
                  <p className="mt-1">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300">
                      {getDepartmentName(selectedIndicator.departmentId)}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Frequency</label>
                  <p className="mt-1 text-base text-gray-900 dark:text-gray-100">{getFrequencyName(selectedIndicator.frequencyId)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Unit</label>
                  <p className="mt-1 text-base font-medium text-gray-900 dark:text-gray-100">{selectedIndicator.unit}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Numerator Label</label>
                  <p className="mt-1 text-base text-gray-900 dark:text-gray-100">{selectedIndicator.numeratorLabel || <span className="text-gray-400">Not specified</span>}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Denominator Label</label>
                  <p className="mt-1 text-base text-gray-900 dark:text-gray-100">{selectedIndicator.denominatorLabel || <span className="text-gray-400">Not specified</span>}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Block Level</label>
                  <p className="mt-1">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                        selectedIndicator.isBlockLevel
                          ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {selectedIndicator.isBlockLevel ? "Yes" : "No"}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Program Type</label>
                  <p className="mt-1">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300">
                      {selectedIndicator.programType || <span className="text-gray-400">Not specified</span>}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</label>
                  <p className="mt-1">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                        selectedIndicator.isActive
                          ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                          : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${selectedIndicator.isActive ? "bg-green-600" : "bg-red-600"}`}></span>
                      {selectedIndicator.isActive ? "Active" : "Inactive"}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Created At</label>
                  <p className="mt-1 text-base text-gray-900 dark:text-gray-100">{formatDate(selectedIndicator.createdAt)}</p>
                </div>
              </div>
              {selectedIndicator.description && (
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Description</label>
                  <p className="mt-1 text-base text-gray-900 dark:text-gray-100 p-3 bg-gray-50 dark:bg-gray-800 rounded-md">{selectedIndicator.description}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="shadow-sm">
        <CardHeader className="border-b bg-gray-50/50 dark:bg-gray-800/50">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <BarChart3 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-2xl">Performance Indicators</CardTitle>
                <CardDescription className="mt-1">Manage and track indicators across departments</CardDescription>
              </div>
            </div>
            <Button onClick={() => {
              setEditingId(null);
              setSelectedDepartment("");
              setSelectedFrequency("");
              reset({
                indicatorName: "",
                departmentId: "",
                frequencyId: "",
                unit: "",
                numeratorLabel: "",
                denominatorLabel: "",
                description: "",
                isBlockLevel: false,
              });
              setError("");
              setShowForm(true);
            }} className="shadow-sm">
              <Plus className="mr-2 h-4 w-4" />
              Add Indicator
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="mb-6 space-y-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Search & Filters</h3>
              {totalCount > 0 && (
                <div className="flex items-center gap-2 px-2.5 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                  <span>{totalCount}</span>
                  <span>Total Indicators</span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
              <div className="relative lg:col-span-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search indicators..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-10"
                />
              </div>
              
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="All Departments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.departmentName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={frequencyFilter} onValueChange={setFrequencyFilter}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="All Frequencies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Frequencies</SelectItem>
                  {frequencies.map((freq) => (
                    <SelectItem key={freq.id} value={freq.id}>
                      {freq.frequencyName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={blockLevelFilter} onValueChange={setBlockLevelFilter}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="All Levels Data" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels Data</SelectItem>
                  <SelectItem value="true">Block Level Data</SelectItem>
                  <SelectItem value="false">District Level Data</SelectItem>
                </SelectContent>
              </Select>

              <Select value={programTypeFilter} onValueChange={setProgramTypeFilter}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="All Programs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Programs</SelectItem>
                  <SelectItem value="Aspirational District Program">Aspirational District Program</SelectItem>
                  <SelectItem value="Aspirational Block Program">Aspirational Block Program</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {(searchQuery || departmentFilter !== "all" || frequencyFilter !== "all" || blockLevelFilter !== "all" || programTypeFilter !== "all") && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600 dark:text-gray-400">Active filters:</span>
                <div className="flex flex-wrap gap-2">
                  {searchQuery && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md text-xs">
                      Search: {searchQuery}
                      <button onClick={() => setSearchQuery("")} className="hover:text-blue-900 dark:hover:text-blue-100">×</button>
                    </span>
                  )}
                  {departmentFilter !== "all" && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-md text-xs">
                      Dept: {departments.find(d => d.id === departmentFilter)?.departmentName}
                      <button onClick={() => setDepartmentFilter("all")} className="hover:text-purple-900 dark:hover:text-purple-100">×</button>
                    </span>
                  )}
                  {frequencyFilter !== "all" && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-md text-xs">
                      Freq: {frequencies.find(f => f.id === frequencyFilter)?.frequencyName}
                      <button onClick={() => setFrequencyFilter("all")} className="hover:text-green-900 dark:hover:text-green-100">×</button>
                    </span>
                  )}
                  {blockLevelFilter !== "all" && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-md text-xs">
                      Level: {blockLevelFilter === "true" ? "Block" : "District"}
                      <button onClick={() => setBlockLevelFilter("all")} className="hover:text-orange-900 dark:hover:text-orange-100">×</button>
                    </span>
                  )}
                  {programTypeFilter !== "all" && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 rounded-md text-xs">
                      Program: {programTypeFilter === "Aspirational District Program" ? "Aspirational District Program" : "Aspirational Block Program"}
                      <button onClick={() => setProgramTypeFilter("all")} className="hover:text-pink-900 dark:hover:text-pink-100">×</button>
                    </span>
                  )}
                  <button
                    onClick={() => {
                      setSearchQuery("");
                      setDepartmentFilter("all");
                      setFrequencyFilter("all");
                      setBlockLevelFilter("all");
                      setProgramTypeFilter("all");
                    }}
                    className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 underline"
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <DataTable
            data={indicators}
            columns={[
              {
                key: "indicatorName",
                header: "Name",
                sortable: true,
                render: (indicator) => (
                  <span className="font-medium text-gray-900 dark:text-gray-100">{indicator.indicatorName}</span>
                ),
              },
              {
                key: "department",
                header: "Department",
                render: (indicator) => (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300">
                    {getDepartmentName(indicator.departmentId)}
                  </span>
                ),
              },
              {
                key: "frequency",
                header: "Frequency",
                render: (indicator) => (
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {getFrequencyName(indicator.frequencyId)}
                  </span>
                ),
              },
              {
                key: "level",
                header: "Level",
                render: (indicator) => (
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      indicator.isBlockLevel
                        ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {indicator.isBlockLevel ? "Block" : "District"}
                  </span>
                ),
              },
              {
                key: "status",
                header: "Status",
                render: (indicator) => <StatusBadge isActive={indicator.isActive} />,
              },
              {
                key: "createdAt",
                header: "Created",
                sortable: true,
                render: (indicator) => (
                  <span className="text-sm text-gray-500 dark:text-gray-400">{formatDate(indicator.createdAt)}</span>
                ),
              },
            ]}
            currentPage={currentPage}
            pageSize={pageSize}
            totalCount={totalCount}
            totalPages={totalPages}
            loading={isLoading}
            sortBy={sortBy}
            sortDescending={sortDescending}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onPageSizeChange={(size) => setPageSize(size)}
            onRefresh={loadData}
            getRowKey={(indicator) => indicator.id}
            actionsRenderer={(indicator) => (
              <div className="flex justify-end gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleViewDetails(indicator)}
                  className="h-8 w-8 p-0 hover:bg-green-50 dark:hover:bg-green-900/20 hover:text-green-600 dark:hover:text-green-400"
                  title="View Details"
                >
                  <Eye className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleEdit(indicator)}
                  className="h-8 w-8 p-0 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400"
                  title="Edit Indicator"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDelete(indicator.id)}
                  className="h-8 w-8 p-0 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400"
                  title="Delete Indicator"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
            emptyMessage={
              searchQuery || departmentFilter !== "all" || frequencyFilter !== "all" || blockLevelFilter !== "all" || programTypeFilter !== "all"
                ? "No indicators match your filters"
                : "No indicators found"
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}
